"use client";

import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered instead of children once a descendant throws. */
  fallback?: React.ReactNode;
  onError?: (error: Error) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains render-time errors from a subtree so they don't escape to the
 * App Router root boundary and blank the whole page.
 *
 * Needed for third-party canvas/WebGL widgets: @splinetool/react-spline stores
 * async load failures in state and re-throws them during render, and offers no
 * onError prop, so a boundary is the only way to keep the page alive.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
